import { CopyOutlined, DeleteOutlined, EyeOutlined, MenuOutlined } from '@ant-design/icons';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Tooltip } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import React, { useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { InputTable } from '~/components/atoms/InputTable';
import { QualityContainer } from '~/components/atoms/QualityContainer';
import {
  GroupTitleInformation,
  ModalBody,
  ModalDefault,
  ModalFooter,
  ModalHeader,
} from '~/components/Globals';
import { onlyNumber } from '~/helpers/util';
import { changeFocusWithRef } from '~/helpers/util/table';
import { truncateObservation } from '~/helpers/util/truncateDescription';
import useGfpStore, { IKlokReservationTableData } from '~/zustand/gfp/gfp-service-state';
import { SpanLabel, StyledText } from '../TableListOrders/style';
import * as S from './TableConfirmKlokReservations.styled';

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const Row = ({ children, ...props }: RowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props['data-row-key'],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <tr
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      data-row-key={props['data-row-key']}
    >
      {React.Children.map(children, (child, childIndex) => {
        if ((child as React.ReactElement).key === 'sort') {
          return (
            <React.Fragment key={childIndex}>
              {React.cloneElement(child as React.ReactElement, {
                children: (
                  <MenuOutlined
                    ref={setActivatorNodeRef}
                    style={{ touchAction: 'none', cursor: 'move' }}
                    {...listeners}
                  />
                ),
              })}
            </React.Fragment>
          );
        }
        return child;
      })}
    </tr>
  );
};

export default function TableKlokReservations() {
  const {
    klokReservationsSelected,
    setData,
    observationModal,
    setKlokReservationsSelected,
    duplicateKlokReservation,
    removeKlokReservation,
    handleChangeKlokReservation,
    setObservationModal,
  } = useGfpStore(
    useShallow(state => ({
      klokReservationsSelected: state.klokReservationsSelected,
      setData: state.setData,
      observationModal: state.observationModal,
      setKlokReservationsSelected: state.setKlokReservationsSelected,
      duplicateKlokReservation: state.duplicateKlokReservation,
      removeKlokReservation: state.removeKlokReservation,
      handleChangeKlokReservation: state.handleChangeKlokReservation,
      setObservationModal: state.setObservationModal,
    })),
  );

  const focusControlledColumns = useMemo(() => {
    return ['quantityPacking', 'quantityPerPacking', 'unitPrice', 'observationText'];
  }, []);

  const tableRef = useRef<any>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    onlyNumber(e);
    changeFocusWithRef(e, focusControlledColumns.indexOf('quantityPerPacking'), index, tableRef);
  };

  const handleDeleteKlokReservation = (uniqueId: number) => {
    removeKlokReservation(uniqueId);
  };

  const columns: any = [
    {
      title: '',
      align: 'center',
      key: 'sort',
      width: 50,
    },
    {
      title: 'Lotes',
      align: 'center',
      key: 'key',
      width: 50,
      render: (_: string, __: any, index: number) => {
        const letters = ['A', 'B', 'C', 'D', 'E', 'F'];
        return letters[index % letters.length];
      },
    },
    {
      key: 'productDescription',
      title: 'Descrição do produto',
      dataIndex: 'productDescription',
      render: (_: string, record: IKlokReservationTableData) => {
        return (
          <GroupTitleInformation>
            <Tooltip title={record.productDescription}>
              <StyledText>{truncateObservation(record.productDescription)}</StyledText>
            </Tooltip>
            <SpanLabel>{record.productCode}</SpanLabel>
          </GroupTitleInformation>
        );
      },
    },
    {
      key: 'productQuality',
      title: 'Qld.',
      dataIndex: 'productQuality',
      align: 'center',
      width: 50,
      render: (value: any) => {
        return value ? <QualityContainer quality={value}>{value}</QualityContainer> : null;
      },
    },
    {
      key: 'packingCode',
      title: 'Emb.',
      width: 80,
      align: 'center',
      render: (_: any, record: IKlokReservationTableData) => {
        const packingCode = record.packingCode;
        const packingName = record.packingName;
        return packingCode && packingName ? (
          <Tooltip title={`${packingCode} - ${packingName}`}>
            <StyledText>{packingCode}</StyledText>
          </Tooltip>
        ) : null;
      },
    },
    {
      dataIndex: 'totalQuantity',
      key: 'totalQuantity',
      title: () => {
        return (
          <GroupTitleInformation>
            <StyledText>Quantidade</StyledText>
            <StyledText>em Reserva</StyledText>
          </GroupTitleInformation>
        );
      },
      align: 'center',
      width: 100,
      render: (_: string, record: IKlokReservationTableData) => {
        const quantity = record.remainingQuantityPacking;
        return quantity ? <StyledText>{quantity}</StyledText> : null;
      },
    },
    {
      dataIndex: 'quantityPacking',
      key: 'quantityPacking',
      title: 'QE',
      align: 'center',
      width: 50,
      render: (_: string, record: IKlokReservationTableData, index: number) => {
        const packingQuantity = record.packingQuantity ?? '';
        return (
          <InputTable
            id={String(record.id)}
            disabled={record.packingQuantity == null}
            onKeyDown={e => handleKeyDown(e, index)}
            value={packingQuantity}
            onChange={(valueOrEvent: React.ChangeEvent<HTMLInputElement> | string | number) => {
              let value: string;
              if (typeof valueOrEvent === 'string') {
                value = valueOrEvent;
              } else if (typeof valueOrEvent === 'number') {
                value = valueOrEvent.toString();
              } else {
                value = valueOrEvent.target.value || '';
              }
              handleChangeKlokReservation(value, 'packingQuantity', index);
            }}
            style={{ width: '50px', textAlign: 'center' }}
          />
        );
      },
    },
    {
      dataIndex: 'quantityPerPacking',
      key: 'quantityPerPacking',
      title: 'QpE',
      align: 'center',
      width: 50,
      render: (_: string, record: IKlokReservationTableData) => {
        const unitQuantity = record.unitQuantity;

        return <StyledText>{unitQuantity}</StyledText>;
      },
    },
    {
      dataIndex: 'observation',
      title: 'Obs.',
      width: '70px',
      align: 'center',
      render: (_: string, record: IKlokReservationTableData) => {
        const observation = record.observation || '';
        return (
          <>
            {observation && observation.trim() ? (
              <Tooltip title={truncateObservation(observation)}>
                <EyeOutlined
                  onClick={() => showModal(observation)}
                  style={{
                    cursor: 'pointer',
                    fontSize: '18px',
                    color: '#ef912b',
                  }}
                />
              </Tooltip>
            ) : (
              <h1 style={{ color: '#666666cf', fontSize: '18px' }}>X</h1>
            )}
          </>
        );
      },
    },
    {
      key: 'observationId1',
      title: 'Obs 1.',
      dataIndex: 'qualityObservationId1',
      align: 'center',
      width: 60,
      render: (_: any, record: IKlokReservationTableData) => {
        const observationCode1 = record.qualityObservationCode1;
        const observationDescription1 = record.qualityObservationDescription1;
        return observationCode1 && observationDescription1 ? (
          <Tooltip title={`${observationCode1} - ${observationDescription1}`}>
            <StyledText>{observationCode1}</StyledText>
          </Tooltip>
        ) : null;
      },
    },
    {
      key: 'observationId2',
      title: 'Obs 2.',
      dataIndex: 'qualityObservationId2',
      align: 'center',
      width: 60,
      render: (_: any, record: IKlokReservationTableData) => {
        const observationCode2 = record.qualityObservationCode2;
        const observationDescription2 = record.qualityObservationDescription2;
        return observationCode2 && observationDescription2 ? (
          <Tooltip title={`${observationCode2} - ${observationDescription2}`}>
            <StyledText>{observationCode2}</StyledText>
          </Tooltip>
        ) : null;
      },
    },
    {
      title: '',
      key: 'actions',
      dataIndex: 'actions',
      width: 30,
      render: (_text: string, record: IKlokReservationTableData, index: number) => {
        const isKlokReservation = 'lines' in record;
        return (
          <span>
            <Button
              type="default"
              icon={<CopyOutlined />}
              onClick={() => duplicateKlokReservation(record.uniqueId)}
              style={{ marginRight: '5px' }}
            />
            <Button
              type="default"
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteKlokReservation(record.uniqueId)}
            />
          </span>
        );
      },
    },
  ];

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!active.id || !over?.id) {
      console.error('IDs inválidos:', { activeId: active.id, overId: over?.id });
      return;
    }

    if (active.id !== over.id) {
      const oldIndex = klokReservationsSelected.findIndex(item => item.uniqueId === active.id);
      const newIndex = klokReservationsSelected.findIndex(item => item.uniqueId === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        console.error('Índices inválidos:', { oldIndex, newIndex });
        return;
      }

      const newItems = arrayMove(klokReservationsSelected, oldIndex, newIndex);
      setKlokReservationsSelected(newItems);
    }
  };

  const showModal = (text: string) => {
    setObservationModal(true);
    setData(state => {
      state.observationModal.text = text;
    });
  };

  const handleCancelObservation = () => {
    setObservationModal(false);
  };

  return (
    <>
      <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
        <SortableContext
          items={klokReservationsSelected.map(item => item.uniqueId)}
          strategy={verticalListSortingStrategy}
        >
          <S.StyledTable
            components={{ body: { row: Row } }}
            rowKey="uniqueId"
            columns={columns}
            dataSource={klokReservationsSelected}
            pagination={false}
          />
        </SortableContext>
      </DndContext>
      <ModalDefault
        width={500}
        visible={observationModal.isOpen}
        onCancel={handleCancelObservation}
      >
        <ModalHeader style={{ marginBottom: '5px' }}>
          <span>Observação da Reserva</span>
        </ModalHeader>
        <ModalBody style={{ marginTop: '5px', marginBottom: '5px' }}>
          <TextArea rows={4} disabled value={observationModal.text} />
        </ModalBody>
        <ModalFooter
          style={{
            marginTop: '5px',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button key="cancel" onClick={handleCancelObservation}>
            Cancelar
          </Button>
        </ModalFooter>
      </ModalDefault>
    </>
  );
}
